import {
  LayoutDashboard,
  Users,
  Shield,
  ShieldCheck,
  Gavel,
  Ban,
  ListChecks,
  Terminal,
  ScrollText,
  Award,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { useActiveGroupCapabilities } from '@/stores/gameGroups.store';
import { usesGameGroup } from '@/utils/gameGroups';
import { useModuleEnabled, type ProjectPageKey } from '@/stores/projectPages.store';
import { type FeatureKey } from '@/config/games';
import { DASHBOARD_PATH, dashboardCards, opensWhenOff, satelliteServes, type PagesWhenOff } from '@/config/panelModules';
import { useSettingsSections, type SettingsSection } from '@/config/settingsSections';

/**
 * Entrées de la barre latérale, et règle qui décide si l'une d'elles s'affiche. La navigation de
 * Supervision vit ici plutôt que dans Sidebar : le bandeau (onglet Supervision) et l'accueil du
 * panel (route `/`) y cherchent la première page ouverte.
 */
export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  feature?: FeatureKey;
  adminOnly?: boolean;   // admin total uniquement
  manageOnly?: boolean;  // admin OU gestion déléguée (manageAccess / canAssignGrades)
  page?: string; // clé de page du grade (ex. "sanctions", "config.blocks") : gating fin, scopé au projet actif
  // Module du panel, activable globalement ET projet par projet (Paramètres généraux >
  // Fonctionnalités). Distinct de `page` : `page` est une permission de grade, `projectPage` est un
  // réglage d'existence. L'entrée n'apparaît que si les DEUX niveaux l'activent (et son module
  // parent, pour une section). Les modules propres à un projet (Mondes, Pays des statistiques,
  // Discord) le sont par leur défaut côté serveur, et non plus par une liste de jeux codée ici.
  projectPage?: ProjectPageKey;
  // Pages fusionnées en une seule entrée à onglets : l'entrée s'affiche dès qu'un des droits est
  // présent, et la page se charge de masquer l'onglet dont le droit manque.
  anyPage?: string[];
  end?: boolean; // NavLink exact match (utile pour un lien d'accueil d'espace)
  // URL supplémentaires qui doivent allumer l'entrée. Nécessaire pour une entrée à onglets : ses
  // onglets ont leur propre URL, et `end: true` seul ne surlignerait que l'URL du premier.
  alsoActiveOn?: string[];
  // Section des Paramètres généraux : l'entrée n'apparaît que si la section est ouverte au grade
  // (config/settingsSections, même règle que la garde de route).
  settingsSection?: SettingsSection;
  // Droits qui ouvrent aussi l'entrée tant qu'un autre module est coupé : l'écran reprend alors des
  // réglages dont la place habituelle est fermée (même règle que l'onglet du bandeau).
  pagesWhenOff?: PagesWhenOff;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

// Dashboard : page d'accueil, visible pour un utilisateur connecté (l'accès est garanti par le guard),
// sauf sous un groupe dont le satellite ne sert aucune de ses cartes (cf dashboardCards).
export const dashboardItem: NavItem = {
  path: DASHBOARD_PATH,
  label: 'Dashboard',
  icon: LayoutDashboard,
  feature: 'dashboard',
};

export const navGroups: NavGroup[] = [
  {
    title: 'Information',
    items: [
      { path: '/logs', label: 'Logs', icon: ScrollText, feature: 'logs', page: 'logs', projectPage: 'logs' },
      // Statistiques a quitté ce groupe : c'est désormais un espace à part entière, au même titre
      // que Supervision, Configuration et Déploiement (voir SPACES dans Header.tsx).
      { path: '/console', label: 'Server', icon: Terminal, feature: 'console', page: 'console', projectPage: 'console' },
    ],
  },
  {
    title: 'Modération',
    items: [
      { path: '/sanctions', label: 'Sanctions', icon: Gavel, feature: 'sanctions', page: 'sanctions', projectPage: 'sanctions' },
      { path: '/banwords', label: 'Mots interdits', icon: Ban, feature: 'banwords', page: 'banwords', projectPage: 'banwords' },
      { path: '/whitelist', label: 'Whitelist', icon: ListChecks, feature: 'whitelist', page: 'whitelist', projectPage: 'whitelist' },
    ],
  },
  {
    title: 'Gestion',
    items: [
      { path: '/staff', label: 'Staff', icon: Shield, feature: 'staff', page: 'staff', projectPage: 'staff' },
      { path: '/players', label: 'Joueurs', icon: Users, feature: 'players', page: 'players', projectPage: 'players' },
      // Grades in-game (LuckPerms) : un cadre par groupe de jeu. L'ancienne clé config.grades ouvre
      // aussi la page, en lecture seule (cf hasPage et canWrite) : aucun grade existant ne perd l'entrée.
      { path: '/grades', label: 'Grades', icon: Award, feature: 'ranks', page: 'grades', projectPage: 'grades' },
      { path: '/admin', label: 'Grades & accès', icon: ShieldCheck, manageOnly: true },
    ],
  },
];

/**
 * Règle d'affichage d'une entrée : droits du grade, modules du projet, sections des Paramètres,
 * fonctionnalités du jeu, et capacités du satellite du groupe actif (config/panelModules) : une page
 * que ce satellite ne sert pas est masquée. Capacités inconnues (Paladium, Recube, groupes pas encore
 * lus) : ce dernier terme est toujours vrai, la navigation reste celle d'avant.
 */
export function useNavItemVisible(): (item: NavItem) => boolean {
  const user = useAuthStore((s) => s.user);
  const hasPage = useAuthStore((s) => s.hasPage);
  const hasFeature = useGameStore((s) => s.hasFeature);
  // hasPage et hasFeature lisent le projet actif à l'appel : abonnement pour redessiner au changement.
  useGameStore((s) => s.activeGameId);
  // Global ET projet ET parent, pour le projet actif ; se redessine quand les réglages arrivent.
  const isModuleOn = useModuleEnabled();
  const settingsSections = useSettingsSections();
  const capabilities = useActiveGroupCapabilities();
  return (item: NavItem) =>
    (!item.adminOnly || !!user?.isAdmin) &&
    (!item.manageOnly || !!user?.isAdmin || !!user?.manageAccess) &&
    (!item.page || hasPage(item.page) || opensWhenOff(item.pagesWhenOff, hasPage, isModuleOn)) &&
    (!item.anyPage || item.anyPage.some((pg) => hasPage(pg))) &&
    (!item.projectPage || isModuleOn(item.projectPage)) &&
    (!item.settingsSection || settingsSections.includes(item.settingsSection)) &&
    (!item.feature || hasFeature(item.feature)) &&
    satelliteServes(item.path, capabilities);
}

/**
 * Page d'arrivée de la Supervision (route `/`, onglet Supervision du bandeau) : le Dashboard, comme
 * avant, sauf sous un groupe dont le satellite ne sert aucune de ses cartes : la première page
 * ouverte de la navigation qui montre le sélecteur de groupe (Server, Grades, Grades & accès...),
 * à défaut la première page ouverte (Logs), et à défaut le Dashboard, qui dit alors pourquoi il
 * est vide.
 */
export function useSupervisionLanding(): string {
  const canShow = useNavItemVisible();
  const capabilities = useActiveGroupCapabilities();
  if (dashboardCards(capabilities).any) return DASHBOARD_PATH;
  const visible = navGroups.flatMap((g) => g.items).filter(canShow);
  // Une page qui dépend du groupe d'abord : elle montre le sélecteur, qui explique la navigation
  // filtrée et permet d'en changer. Logs, hors groupe, ne le montre pas.
  const first = visible.find((item) => usesGameGroup(item.path)) ?? visible[0];
  return first?.path ?? DASHBOARD_PATH;
}
